import { useState, useEffect, useCallback } from 'react';
import { Video, ApiError } from '../types';

export interface ChannelInfo {
  channelId: string;
  title: string;
  description: string;
  customUrl: string | null;
  thumbnailUrl: string | null;
  bannerUrl: string | null;
  country: string | null;
  publishedAt: string | null;
  subscriberCount: number | null;
  videoCount: number;
  viewCount: number;
}

export interface ChannelTotals {
  videoCount: number;
  totalViews: number;
  firstSeenAt: number;
}

interface ChannelApiVideo {
  videoId: string;
  title: string;
  channelId: string;
  channelTitle: string;
  thumbnailUrl: string;
  durationSeconds: number;
  viewCount: number;
  isShort: boolean;
  publishedAt: number | null;
  firstSeenAt: number;
  lastSeenAt: number;
}

interface ChannelApiResponse {
  channel: ChannelInfo | null;
  title: string;
  totals: ChannelTotals | null;
  videos: ChannelApiVideo[];
}

interface UseChannelResult {
  channel: ChannelInfo | null;
  /** Best-known display title — falls back to D1's channel_title when the
   *  live API call fails or the channel hides itself, so the page/modal
   *  always has a name to show. */
  title: string | null;
  totals: ChannelTotals | null;
  /** The channel's videos we have ever tracked, ranked by peak views. */
  videos: Video[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

function secondsToDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  let result = 'PT';
  if (hours > 0) result += `${hours}H`;
  if (minutes > 0) result += `${minutes}M`;
  if (secs > 0 || result === 'PT') result += `${secs}S`;
  return result;
}

function mapVideo(item: ChannelApiVideo): Video {
  const published = item.publishedAt ?? item.firstSeenAt;
  return {
    id: item.videoId,
    title: item.title,
    channelTitle: item.channelTitle,
    channelId: item.channelId,
    thumbnail: item.thumbnailUrl,
    viewCount: item.viewCount,
    publishedAt: new Date(published * 1000).toISOString(),
    duration: secondsToDuration(item.durationSeconds),
    durationSeconds: item.durationSeconds,
    isShort: item.isShort,
  };
}

/**
 * Fetch public info + our tracked video history for a single channel. Pass
 * the channelId to load; pass null to clear any previously loaded channel
 * (e.g. when a modal closes).
 */
export function useChannel(channelId: string | null): UseChannelResult {
  const [channel, setChannel] = useState<ChannelInfo | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [totals, setTotals] = useState<ChannelTotals | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChannel = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/channel?id=${encodeURIComponent(id)}`);
      if (!response.ok) {
        const errorData = (await response.json()) as ApiError;
        throw new Error(errorData.error || 'Failed to load channel');
      }
      const data = (await response.json()) as ChannelApiResponse;
      setChannel(data.channel);
      setTitle(data.title);
      setTotals(data.totals);
      setVideos(data.videos.map(mapVideo));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setChannel(null);
      setTitle(null);
      setTotals(null);
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!channelId) {
      setChannel(null);
      setTitle(null);
      setTotals(null);
      setVideos([]);
      setError(null);
      return;
    }
    fetchChannel(channelId);
  }, [channelId, fetchChannel]);

  const refetch = useCallback(() => {
    if (channelId) fetchChannel(channelId);
  }, [channelId, fetchChannel]);

  return { channel, title, totals, videos, isLoading, error, refetch };
}
